import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { hasActiveLiveTv } from '../lib/liveTv'
import '../components/prediction/predictionModern.css'
import { usePredictionUserEntry } from '../hooks/usePredictionUserEntry'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernDashboardPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

/** หน้า Dashboard (แปลงจาก profile.html) — แสดงรหัสสมาชิก, ยอดเครดิต, คะแนน, ปุ่มฝาก/ถอน, เมนู */
const ModernDashboardPage: React.FC<ModernDashboardPageProps> = ({
  themeConfig,
  assetConfig: _assetConfig,
}) => {
  const navigate = useNavigate()
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
      authService.fetchUserBankAccounts(),
    ]).then(([walletRes, summary, bankAccounts]) => {
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
      setUserSummary(summary ?? null)
      setUserBankAccounts(bankAccounts ?? [])
    })
  }, [])

  useEffect(() => {
    const handleUserServiceAccessUpdated = () => {
      if (!authService.isAuthenticated()) return
      authService.fetchUserInfoSummary().then((summary) => {
        setUserSummary(summary ?? null)
      })
    }

    window.addEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
    return () => window.removeEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
  }, [])

  // เมื่อแอดมินเติม/หักเครดิต WebSocket ส่ง balance_notification → รีเฟรชยอดในหน้านี้
  useEffect(() => {
    const handleBalanceNotification = () => {
      if (authService.isAuthenticated()) {
        walletService.getWallet().then((res) => {
          if (res.success && res.data) setWalletData(res.data)
        })
      }
    }
    window.addEventListener('balance_notification', handleBalanceNotification)
    return () => window.removeEventListener('balance_notification', handleBalanceNotification)
  }, [])

  const user = authService.getCurrentUser()
  const memberId = user?.username ?? '—'
  const displayName = user?.username ?? 'สมาชิก'
  const balanceText = walletData != null
    ? walletService.formatCurrency(walletService.getDisplayBalance(walletData)) + ' ฿'
    : '0.00 ฿'
  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} pts` : '0.00 pts'
  const primaryBank = userBankAccounts[0] ?? null
  const userBankSummaryText = primaryBank
    ? `${primaryBank.accountNumber} | ${primaryBank.bankName}`
    : 'ยังไม่ได้ผูกบัญชี'
  // ปิดทั้งระบบระดับ tenant (ปิดปรับปรุงระบบ)
  const tenantLotteryEnabled = themeConfig?.settings?.lotteryEnabled !== false
  const tenantCasinoEnabled = themeConfig?.settings?.casinoEnabled !== false
  // เข้าเล่นได้จริงเมื่อเปิดทั้ง tenant และสิทธิ์ระดับ user
  const isLotteryEnabled = tenantLotteryEnabled && authService.isManagedServiceEnabled(userSummary?.serviceConfigs, 'lottery')
  const isCasinoEnabled = tenantCasinoEnabled && authService.isManagedServiceEnabled(userSummary?.serviceConfigs, 'casino')
  const predictionEnabled = themeConfig?.settings?.predictionEnabled === true
  const { showPredictionEntry } = usePredictionUserEntry(predictionEnabled)
  const showLiveTv = hasActiveLiveTv(
    themeConfig?.settings?.liveTvChannels || [],
    themeConfig?.settings?.liveTvEnabled,
    themeConfig?.settings?.huayYoutubeLive
  )

  const menuItems = [
    { label: 'ประวัติฝาก-ถอนเงิน', href: '/history', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
    { label: 'แนะนำเพื่อนรับค่าคอม', href: '/affiliate', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
    { label: 'รับเครดิตเงินคืน', href: '/cashback', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
    { label: 'โบนัส', href: '/promotion', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
    ...(showLiveTv
      ? [{ label: 'ดูถ่ายทอดสด', href: '/live-tv', className: 'item_menu_p border_30 bg_op_secondary', isLink: true }]
      : []),
    { label: 'ข้อมูลบัญชี', href: '/member', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
    { label: 'คู่มือ', href: '/howto', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
    { label: 'รางวัลประจำเดือน', href: '/reward', className: 'item_menu_p border_30 bg_op_secondary', isLink: true },
  ]

  const handleLogout = async () => {
    await authService.logout()
    navigate('/')
  }

  return (
    <>
      <div className="content dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-10 offset-lg-1 dashboard-content-col">
              <div className="c_quinary text-center">
                <h3 className="font_md">
                  รหัสสมาชิกของคุณ : <span className="c_tertiary font_lg">{memberId}</span>
                </h3>
                <h4 className="font_md">
                  สวัสดีคุณ : <span className="c_tertiary font_lg">{displayName}</span>
                </h4>
              </div>

              <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3">
                <div className="row align-items-center">
                  <div className="col-12 col-lg-6 mb-4 mb-lg-0">
                    <div className="box_cd mx-auto text-center c_tertiary">
                      <h6>ยอดเครดิตของคุณ</h6>
                      <h1 className="font_md my-3 bg_secondary c_primary border_5">{balanceText}</h1>
                      <h5 className="c_quinary">
                        คะแนนของคุณ : <span className="c_tertiary font_md">{pointsText}</span>
                      </h5>
                      <h6 className="mx-auto" style={{ width: 'fit-content' }}>
                        <Link to="/points" className="c_warning txt_links_primary text-decoration-none">
                          <u>ดูคะแนนทั้งหมด</u>
                        </Link>
                      </h6>
                    </div>

                    <div className="line_btw mt-4 mb-3 mx-auto" />

                    <div className="row mx__1">
                      <div className="col-12 col-md-5 offset-md-1 px-1 mt-2">
                        <Link
                          to="/topup"
                          className="link_pmr box_row justify-content-center font_rg bg_primary gap-3 border_30 ins_sd ft_sd w-100 border-0 text-decoration-none"
                        >
                          <h5 className="c_tertiary mb-0">ฝากเงิน</h5>
                        </Link>
                      </div>
                      <div className="col-12 col-md-5 px-1 mt-2">
                        <Link
                          to="/withdraw"
                          className="link_pmr box_row justify-content-center font_rg bg_secondary gap-3 border_30 ins_sd ft_sd w-100 border-0 text-decoration-none"
                        >
                          <h5 className="c_secondary mb-0">ถอนเงิน</h5>
                        </Link>
                      </div>
                    </div>

                    <h6 className="c_quinary text-center mt-4">ข้อมูลบัญชีของคุณ :</h6>
                    <h5 className="c_tertiary font_md text-center">{userBankSummaryText}</h5>
                  </div>

                  <div className="col-12 col-lg-6">
                    {isLotteryEnabled ? (
                      <Link
                        to="/lobby-huay"
                        className="link_main mb-3 box_row py-3 justify-content-center font_rg bg_gd_quaternary gap-3 border_10 ins_sd ft_sd text-decoration-none"
                      >
                        <h4 className="ft_sd c_tertiary mb-0">เข้าแทงหวย</h4>
                        <h3 className="ft_sd c_tertiary mb-0">
                          <i className="far fa-edit" />
                        </h3>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="link_main mb-3 box_row py-3 justify-content-center font_rg bg_gd_quaternary gap-3 border_10 ins_sd ft_sd w-100 border-0 text-decoration-none opacity-60"
                        disabled
                        title={!tenantLotteryEnabled ? 'ปิดปรับปรุงระบบหวย' : 'ปิดการใช้งานหวยโดยผู้ดูแลระบบ'}
                      >
                        <h4 className="ft_sd c_tertiary mb-0">{!tenantLotteryEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดแทงหวย'}</h4>
                        <h3 className="ft_sd c_tertiary mb-0">
                          <i className="fas fa-ban" />
                        </h3>
                      </button>
                    )}
                    {isCasinoEnabled ? (
                      <Link
                        to="/lobby-casino"
                        className="link_main mb-3 box_row py-3 justify-content-center font_rg bg_gd_tertiary gap-3 border_10 ins_sd ft_sd text-decoration-none"
                      >
                        <h4 className="ft_sd c_tertiary mb-0">เข้าเล่นเกมส์</h4>
                        <h3 className="ft_sd c_tertiary mb-0">
                          <i className="fas fa-dice" />
                        </h3>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="link_main mb-3 box_row py-3 justify-content-center font_rg bg_gd_tertiary gap-3 border_10 ins_sd ft_sd w-100 border-0 text-decoration-none opacity-60"
                        disabled
                        title={!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบเกมส์' : 'ปิดการใช้งานเกมส์โดยผู้ดูแลระบบ'}
                      >
                        <h4 className="ft_sd c_tertiary mb-0">{!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดเข้าเล่นเกมส์'}</h4>
                        <h3 className="ft_sd c_tertiary mb-0">
                          <i className="fas fa-ban" />
                        </h3>
                      </button>
                    )}

                    {showPredictionEntry && (
                      <Link to="/points/games/prediction" className="pred-dashboard-cta">
                        <div className="pred-dashboard-cta__inner">
                          <div className="pred-dashboard-cta__icon">
                            <i className="fas fa-bullseye" />
                          </div>
                          <div className="pred-dashboard-cta__text">
                            <h4>เกมทายผล Prediction</h4>
                            <span>ทายผลลุ้นรางวัล · ดู Odds แบบ Realtime</span>
                          </div>
                        </div>
                      </Link>
                    )}

                    <div className="list_menu_p">
                      {menuItems.map((item) =>
                        (item as { isLink?: boolean }).isLink ? (
                          <Link key={item.label} to={item.href} className={item.className}>
                            {item.label}
                          </Link>
                        ) : (
                          <a key={item.label} href={item.href} className={item.className}>
                            {item.label}
                          </a>
                        )
                      )}
                      <button
                        type="button"
                        className="item_menu_p border_30 border-0 w-100 text-center text-white"
                        style={{ backgroundColor: '#dc2626' }}
                        onClick={handleLogout}
                      >
                        ออกจากระบบ
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
      </div>
    </>
  )
}

export default ModernDashboardPage
